"use client"

import React, { useState } from "react"
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react"
import { PatternFish } from "@/components/ui/pattern-fish"

interface TestimonialItem {
  name: string
  location: string
  text: string
  rating: number
}

interface TestimonialsProps {
  dict: {
    label: string
    title: string
    description: string
    items: TestimonialItem[]
    controls: {
      prev: string
      next: string
      indicator: string
    }
  }
}

export function Testimonials({ dict }: TestimonialsProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const testimonials = dict.items

  const goNext = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length)
  }

  const goPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length)
  }

  return (
    <section id="depoimentos" className="relative overflow-hidden bg-slate-900 py-16 lg:py-24 text-white">
      <PatternFish className="absolute inset-0 z-0 opacity-10" />
      
      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-12">
        {/* Section header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div className="max-w-2xl">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-accent">
              {dict.label}
            </p>
            <h2 className="mt-4 text-4xl font-black tracking-tight text-white md:text-5xl lg:text-6xl text-balance">
              {dict.title}
            </h2>
          </div>
          
          <div className="flex items-center gap-4">
            <button
              onClick={goPrev}
              className="flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-white transition-all hover:bg-accent hover:text-accent-foreground hover:scale-110 active:scale-95"
              aria-label={dict.controls.prev}
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              onClick={goNext}
              className="flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-white transition-all hover:bg-accent hover:text-accent-foreground hover:scale-110 active:scale-95"
              aria-label={dict.controls.next}
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Display */}
        <div className="mt-20">
          <div className="relative overflow-hidden">
            <div 
              className="flex transition-transform duration-700 ease-in-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {testimonials.map((t, idx) => (
                <div key={idx} className="w-full flex-shrink-0">
                  <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
                    <div className="relative">
                      <Quote className="absolute -left-8 -top-8 h-24 w-24 text-accent/10 lg:-left-12 lg:-top-12 lg:h-32 lg:w-32" />
                      <p className="relative z-10 font-serif text-2xl font-medium leading-relaxed italic text-blue-50 md:text-3xl lg:text-4xl">
                        "{t.text}"
                      </p>
                    </div>
                    
                    <div className="flex flex-col gap-6 lg:pl-12 lg:border-l lg:border-white/10">
                      <div className="flex gap-1">
                        {Array.from({ length: t.rating }).map((_, i) => (
                          <Star key={i} className="h-5 w-5 fill-accent text-accent" />
                        ))}
                      </div>
                      <div className="flex flex-col gap-1">
                        <cite className="not-italic text-xl font-black text-white">{t.name}</cite>
                        <span className="text-sm font-bold uppercase tracking-widest text-accent/60">
                          {t.location}
                        </span>
                      </div>
                      
                      <div className="mt-4 flex gap-2">
                        {testimonials.map((_, i) => (
                          <button
                            key={i}
                            onClick={() => setCurrentIndex(i)}
                            className={`h-1.5 rounded-full transition-all duration-500 ${
                              i === currentIndex ? "w-12 bg-accent" : "w-4 bg-white/10"
                            }`}
                            aria-label={dict.controls.indicator.replace("{n}", (i + 1).toString())}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
